import { useTranslation } from 'react-i18next';
import { useState, useContext, useMemo, useEffect, useRef } from 'react';
import { MenuTab, MenuTabs } from './MenuTabs/MenuTabs';
import { Typography } from 'app/components/ui';
import { Taxonomy } from './taxonomy/Taxonomy';
import { buildUsedConceptRoles } from './taxonomy/usedConceptRoles';
import { AllTags } from './allTags/AllTags';
import { TagInfo } from './tagInfo/TagInfo';
import { ExcludePanel } from './ExcludePanel';
import { FootnotePanel } from './FootnotePanel';
import { DetailsModal, DetailsTagConcept } from './Modal/DetailsModal';
import { DeleteTagModal } from './Modal/DeleteTagModal';
import { useSlateStatic } from 'slate-react';
import { TagWithMetadata, TaxonomyChild, useTagsByDocument, useDocumentTaxonomyConfig } from 'entities/tag';
import { useShadowSelectionMarked } from './useShadowSelectionMarked';
import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { useTagDisplay, useTagSelection, useTagNav } from 'app/state/contexts/TagContext';
import { useCalcPick } from 'app/state/contexts/CalcPickContext';
import { NewExtensionModal } from '../../subtoolbar/tags/NewExtension/NewExtensionModal/NewExtensionModal';
import { useDeleteTagWithConfirmation } from './useDeleteTag';
import { buildChainContaining } from './Footer/tagChainUtils';
import { TagLanguageDropdown } from './TagLanguageDropdown';
import { resolveConceptLabel } from 'app/utils/tag-utils';
import { usePermissions } from 'app/api/permission-queries';
import { useEditorDocument } from 'app/api/documents/useEditorData';

const isTaxonomyChild = (sel: TaxonomyChild | TagWithMetadata): sel is TaxonomyChild => 'children' in sel;

export const Tags = () => {
  const { t } = useTranslation(['lang', 'tag', 'taxonomy']);

  const { activeTagsTab, setActiveTagsTab } = useContext(SidebarContext);
  const { clickedTagIds, clickedFromContent, setClickedTagIds, tableSelection } = useTagSelection();
  const { calcPickMode } = useCalcPick();
  const { tagLanguage } = useTagDisplay();
  const { selectedFootnoteId, setSelectedFootnoteId } = useTagNav();
  const [selection, setSelection] = useState<(TaxonomyChild & { nodePath?: string }) | TagWithMetadata | null>(null);
  const editor = useSlateStatic();
  const isContentMarked = useShadowSelectionMarked();
  const { data: pDocument } = useEditorDocument();
  const { documentPermissions } = usePermissions(pDocument?.superId);
  const canEditTags = documentPermissions.includes('TAG_EDITOR') || documentPermissions.includes('TAG_ADMIN');
  const { data: tagsResponse } = useTagsByDocument(pDocument?.superId, { include: ['concept', 'metadata', 'unit', 'context'] });
  const documentTags = tagsResponse?.tags;
  const { data: taxonomyConfig } = useDocumentTaxonomyConfig(pDocument?.superId);
  const activeTaxonomyIdForExtensions =
    taxonomyConfig?.taxonomySelections?.find((t) => t.isActive)?.taxonomyId ?? taxonomyConfig?.taxonomySelections?.[0]?.taxonomyId;

  // Unified active tab state for better control over tab switching logic
  const activeTab = activeTagsTab as 'taxonomy' | 'allTags' | 'tagInfo';

  // Derive typed selections from the unified state
  const selectedTaxonomyTag = selection && isTaxonomyChild(selection) ? selection : null;
  const selectedDocTagId = selection && !isTaxonomyChild(selection) ? selection.id : null;

  // Derive selectedDocTag from the query cache so it's always fresh after mutations
  const selectedDocTag = useMemo(() => {
    if (!selectedDocTagId || !documentTags) return null;
    return documentTags.find((t) => t.id === selectedDocTagId) ?? null;
  }, [selectedDocTagId, documentTags]);

  // When a taxonomy tag is selected that's used in the document, resolve the matching doc tag
  const effectiveDocTag = useMemo(() => {
    if (selectedDocTag) return selectedDocTag;
    if (!selectedTaxonomyTag || !documentTags) return null;
    return documentTags.find((t) => t.concept?.name === selectedTaxonomyTag.id && !t.metadata?.unplaced) ?? null;
  }, [selectedDocTag, selectedTaxonomyTag, documentTags]);

  // Role-aware used state: concept name -> link roles it is tagged under
  const usedConceptRoles = useMemo(() => buildUsedConceptRoles(documentTags ?? []), [documentTags]);

  // React to tag clicks from the editor
  const [filterTagIds, setFilterTagIds] = useState<string[] | null>(null);
  const [appliedTagIds, setAppliedTagIds] = useState<string[] | null>(null);

  // Marking a new cell starts over on the taxonomy tab. A concept picked on the taxonomy tab is kept so concept-first
  // tagging works. Declared before the tag-click effect so a tagged cell, or cells just tagged, still land on tagInfo.
  const tableSelectionKey = tableSelection?.cells?.length
    ? `${tableSelection.referenceDataSuperId}:${(tableSelection.cells as { row: number; col: number }[]).map((c) => `${c.row},${c.col}`).join('|')}`
    : null;

  useEffect(() => {
    if (!tableSelectionKey) return;
    setAppliedTagIds(null);
    if (calcPickMode || (activeTagsTab === 'taxonomy' && selectedTaxonomyTag)) return;
    setSelection(null);
    setFilterTagIds(null);
    setSelectedFootnoteId(null);
    setActiveTagsTab('taxonomy');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableSelectionKey]);

  useEffect(() => {
    if (!clickedTagIds || clickedTagIds.length === 0) return;
    if (activeTagsTab === 'taxonomy' && selectedTaxonomyTag && clickedFromContent) {
      setClickedTagIds(null);
      return;
    }
    if (!documentTags) return; // wait for documentTags to load before switching tab

    const liveIds = clickedTagIds.filter((id: string) => documentTags.some((t) => t.id === id));
    if (liveIds.length === 0) return;

    if (liveIds.length === 1) {
      const docTag = documentTags.find((t) => t.id === liveIds[0]) ?? null;
      setSelection(docTag);
      setFilterTagIds(null);
      setSelectedFootnoteId(null);
      setActiveTagsTab('tagInfo');
    } else {
      setSelection(null);
      setFilterTagIds(liveIds);
      setActiveTagsTab('allTags');
    }

    setClickedTagIds(null);
  }, [clickedTagIds, documentTags, setActiveTagsTab, setClickedTagIds, setSelectedFootnoteId, activeTagsTab, selectedTaxonomyTag, clickedFromContent]);

  useEffect(() => {
    if (!appliedTagIds || !documentTags?.some((t) => appliedTagIds.includes(t.id))) return;
    setClickedTagIds(appliedTagIds);
    setAppliedTagIds(null);
  }, [appliedTagIds, documentTags, setClickedTagIds]);

  useEffect(() => {
    if (selectedFootnoteId) setActiveTagsTab('tagInfo');
  }, [selectedFootnoteId, setActiveTagsTab]);

  const [modalState, setModalState] = useState<{
    show: boolean;
    tagConcept: DetailsTagConcept | null;
  }>({ show: false, tagConcept: null });
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [extensionWiderAnchorSeed, setExtensionWiderAnchorSeed] = useState<string | null>(null);

  // Delete tag flow
  const {
    showModal: showDeleteModal,
    pendingCount,
    chainCount,
    isDeleting,
    requestDelete,
    requestDeleteById,
    requestDeleteMany,
    confirmDelete,
    confirmDeleteChain,
    cancelDelete,
  } = useDeleteTagWithConfirmation({
    editor,
    documentSuperId: pDocument?.superId,
    onDeleted: (tagIds) => {
      const deletedIds = new Set(tagIds);
      if (selectedDocTagId && !deletedIds.has(selectedDocTagId) && documentTags?.some((t) => t.id === selectedDocTagId)) return;
      const remaining = documentTags?.filter(
        (t) => !deletedIds.has(t.id) && !t.metadata?.unplaced && (t.hidden || !t.metadata || !!t.metadata.blockCarrier || t.metadata.textSnippet !== undefined)
      );
      const predecessor = remaining?.find((t) => t.nextSegment && deletedIds.has(t.nextSegment));
      const successor = remaining?.find((t) => documentTags?.some((d) => deletedIds.has(d.id) && d.nextSegment === t.id));
      const neighbour = predecessor ?? successor ?? null;
      setSelection(activeTab === 'taxonomy' ? null : neighbour);
      if (activeTab === 'tagInfo' && !neighbour) {
        setActiveTagsTab('allTags');
      }
    },
  });

  // Expose requestDeleteById for the Slate plugin via a ref on the editor
  const deleteTagRef = useRef<((tagId: string) => void) | null>(null);
  deleteTagRef.current = requestDeleteById;
  useEffect(() => {
    editor.__onTagAboutToBeEmpty = (tagId: string) => {
      deleteTagRef.current?.(tagId);
    };
  }, [editor]);

  const requestDeleteWithChain = (tag: TagWithMetadata) => requestDelete(tag, buildChainContaining(documentTags ?? [], tag.id));

  const tabs: MenuTab[] = [
    { key: 'taxonomy', label: t('taxonomy:title') },
    { key: 'allTags', label: t('tag:tags.label') },
    { key: 'tagInfo', label: t('tag:info.title') },
  ];

  const onDetailsClick = (tag: TaxonomyChild | TagWithMetadata) => {
    if ('conceptType' in tag) {
      setModalState({
        show: true,
        tagConcept: {
          name: tag.id,
          conceptType: tag.conceptType,
          label: resolveConceptLabel({ languages: tag.labels, currentLang: tagLanguage }),
        },
      });
    } else {
      setModalState({
        show: true,
        tagConcept: {
          id: tag.id,
          name: tag.concept?.name ?? '',
          conceptType: tag.concept?.conceptType,
          label: resolveConceptLabel({ languages: tag.concept?.labels ?? {}, currentLang: tagLanguage }),
          notes: tag?.notes ?? undefined,
        },
      });
    }
  };

  const onCreateExtension = (tagIdOrConceptName: string) => {
    // Called from two places with different arg shapes:
    //   - `TagListItem` passes a tag UUID (we resolve to the tag's concept name)
    //   - `TaxonomyFooter` "extend from concept" passes `selectedTag.id`,
    //     which is already a concept QName (e.g. `ifrs-full:Revenue`)
    // Fall through to treating the arg as a concept name when no tag matches.
    const tag = documentTags?.find((t) => t.id === tagIdOrConceptName);
    setExtensionWiderAnchorSeed(tag?.concept?.name ?? tagIdOrConceptName);
    setIsExtensionModalOpen(true);
  };

  return (
    <div className="w-100 pt-2 bg-white h-100 d-flex flex-column">
      <div
        className="d-flex flex-row px-3"
        style={{ alignItems: 'center', justifyContent: 'space-between', gap: '8px', paddingBottom: '12px', paddingTop: '8px' }}
      >
        <Typography variant="body-7" fontWeight="bold">
          {t('tag:tagging')}
        </Typography>

        <TagLanguageDropdown documentSuperId={pDocument?.superId} />
      </div>

      <MenuTabs tabs={tabs} activeTab={activeTab} onTabChange={(key) => setActiveTagsTab(key as typeof activeTab)} />

      <ExcludePanel />

      {/* Content based on activeTab */}

      {activeTab === 'taxonomy' && (
        <Taxonomy
          selectedTagId={selectedTaxonomyTag?.id ?? effectiveDocTag?.concept?.name ?? null}
          preferredNodePath={selectedTaxonomyTag?.nodePath ?? null}
          preferredLinkroleUri={effectiveDocTag?.linkroleUri}
          isContentMarked={canEditTags ? isContentMarked : false}
          usedConceptRoles={usedConceptRoles}
          documentTags={documentTags ?? []}
          onSelectionChange={(tag, nodePath) => setSelection(tag ? { ...tag, nodePath } : null)}
          onDetailsClick={onDetailsClick}
          onExtensionClick={canEditTags ? onCreateExtension : undefined}
          onCellTagsApplied={(tagIds) => {
            setSelection(null);
            setAppliedTagIds(tagIds);
          }}
          onHiddenTagCreated={(tag: unknown) => {
            setSelection(tag as TagWithMetadata);
            setSelectedFootnoteId(null);
            setActiveTagsTab('tagInfo');
          }}
        />
      )}
      {activeTab === 'allTags' && (
        <AllTags
          selectedTag={effectiveDocTag}
          onSelectTag={(tag) => {
            setSelectedFootnoteId(null);
            setSelection(tag && effectiveDocTag?.id === tag.id ? null : tag ?? null);
          }}
          onTagDetailsClick={onDetailsClick}
          onExtensionClick={canEditTags ? onCreateExtension : undefined}
          onDeleteClick={canEditTags ? requestDeleteWithChain : undefined}
          filterTagIds={filterTagIds}
        />
      )}
      {activeTab === 'tagInfo' &&
        (selectedFootnoteId ? (
          <FootnotePanel />
        ) : (
          <TagInfo
            selectedConceptName={selectedTaxonomyTag?.id ?? null}
            selectedTag={effectiveDocTag}
            onDeleteClick={canEditTags ? requestDeleteWithChain : undefined}
            onDeletePartClick={canEditTags ? requestDelete : undefined}
            onDeleteChainClick={canEditTags ? requestDeleteMany : undefined}
            onSelectTag={(tag) => setSelection(tag)}
            isContentMarked={canEditTags ? isContentMarked : false}
            documentTags={documentTags ?? []}
            readOnly={!canEditTags}
            onCancel={() => {
              setSelection(null);
              setActiveTagsTab('allTags');
            }}
          />
        ))}

      <DetailsModal show={modalState.show} tagConcept={modalState.tagConcept} onClose={() => setModalState({ show: false, tagConcept: null })} />

      {pDocument?.superId && activeTaxonomyIdForExtensions && (
        <NewExtensionModal
          show={isExtensionModalOpen}
          onClose={() => {
            setIsExtensionModalOpen(false);
            setExtensionWiderAnchorSeed(null);
          }}
          documentSuperId={pDocument.superId}
          taxonomyId={activeTaxonomyIdForExtensions}
          widerAnchorSeed={extensionWiderAnchorSeed}
        />
      )}

      <DeleteTagModal
        show={showDeleteModal}
        onConfirm={confirmDelete}
        onConfirmChain={confirmDeleteChain}
        onCancel={cancelDelete}
        busy={isDeleting}
        count={pendingCount}
        chainCount={chainCount}
        title={pendingCount > 1 ? t('tag:delete.modal.title') : undefined}
        message={pendingCount > 1 ? t('tag:delete.chain.all.message', { count: pendingCount }) : undefined}
      />
    </div>
  );
};
